import {ControllerParams, IControllerConfig} from '@wix/yoshi-flow-editor';
import {IWidgetControllerConfig} from '@wix/native-components-infra';
import {BaseController, EcomPlatformViewerScriptContext} from '@wix/wixstores-client-storefront-sdk';
import {IAddToCartOptions} from '../../../types/product';
import {FedopsInteraction} from '../../../constants';
import {IAddToCartItem, STORES_APP_DEF_ID} from '@wix/wixstores-client-core';
import {Scope} from '@wix/app-settings-client/dist/src/domain';
import {CartIconStore} from '../../../domain/stores/CartIconStore';
import {ICartIconStyleParams} from '../../../types/app-types';
import {ViewerContext} from '@wix/ecom-platform-storefront-common';
import {BIService} from '../../../domain/services/BIService';

export class CartIconController extends BaseController {
  protected cartIconStore!: CartIconStore;
  protected controllerConfig!: IWidgetControllerConfig;
  protected currentCartService!: EcomPlatformViewerScriptContext['currentCartService'];
  private readonly viewerContext: ViewerContext;
  private readonly biService: BIService;
  constructor(controllerParams: ControllerParams) {
    super(controllerParams);
    this.flowAPI.fedops.interactionStarted(FedopsInteraction.CART_ICON_LOADED);
    this.controllerConfig = controllerParams.controllerConfig;
    this.viewerContext = new ViewerContext(this.controllerConfig);
    this.biService = new BIService(this.viewerContext, this.controllerConfig);
    this.setStoresAndCreateCartExports(controllerParams);
  }

  private setStoresAndCreateCartExports({controllerConfig, flowAPI, appData}: ControllerParams) {
    this.currentCartService = appData?.context.currentCartService;
    this.cartIconStore = new CartIconStore(
      this.siteStore,
      this.viewerContext,
      controllerConfig.config,
      controllerConfig.setProps,
      flowAPI.reportError,
      flowAPI.translations,
      flowAPI.fedops,
      flowAPI.panoramaClient,
      this.currentCartService,
      this.biService,
      this.controllerConfig.platformAPIs.storage.memory,
      this.controllerConfig.platformAPIs.pubSub
    );

    if (
      this.viewerContext.getDeviceType() === 'Mobile' &&
      controllerConfig.config.publicData?.APP?.DONT_SHOW_SIDE_CART_ON_MOBILE
    ) {
      this.controllerConfig.platformAPIs.storage.memory.setItem('dontShowSideCartOnMobile', 'true');
    }
  }

  public readonly load = async () => {
    await this.cartIconStore.init();
  };

  public readonly init = async (): Promise<void> => {
    await this.load();
  };

  public onStyleUpdate = (newStyleParams: ICartIconStyleParams, publicData?: IControllerConfig['publicData']) => {
    this.cartIconStore.updateStyleParams(newStyleParams, publicData);
  };

  public readonly onAppSettingsUpdate = (updates: {[key: string]: any}) => {
    if (updates.scope === Scope.COMPONENT && updates.source === 'app-settings') {
      this.cartIconStore.updateAppSettings(updates.payload);
    } else if (updates.details) {
      this.cartIconStore.updateAppSettings({main: updates.details});
    }
  };

  public readonly onBeforeUnLoad = () => {
    this.cartIconStore.unSubscribeAll();
  };

  public readonly exports = () => {
    return {
      addToCart: async (productId: string, quantity: number = 1, options: IAddToCartOptions = {}): Promise<boolean> => {
        await this.cartIconStore.executeWithFedops(FedopsInteraction.ADD_TO_CART, async () => {
          const storesAPI = await this.flowAPI.controllerConfig.wixCodeApi.site.getPublicAPI(STORES_APP_DEF_ID);
          return storesAPI.cart.addToCart(productId, quantity, options);
        });
        return true;
      },
      addProductsToCart: async (cartItems: IAddToCartItem[]): Promise<boolean> => {
        await this.cartIconStore.executeWithFedops(FedopsInteraction.ADD_ITEMS_TO_CART, async () => {
          const storesAPI = await this.flowAPI.controllerConfig.wixCodeApi.site.getPublicAPI(STORES_APP_DEF_ID);
          return storesAPI.cart.addProducts(cartItems);
        });
        return true;
      },
    };
  };

  /* istanbul ignore next */
  public getFreeTexts(): string[] {
    return [];
  }
}
